/**
 * Local photo validation — mirrors the server's gate in api/src/routes/
 * generations.ts so the user sees an inline error the instant they pick
 * a bad file (instead of finding out 30s later when Pay → upload fails
 * server-side).
 *
 * Two phases (cheap → expensive):
 *
 *   1. Synchronous (mime + size) — instant, runs in `validatePhotoSync`.
 *      Reject without committing the File / showing a preview.
 *
 *   2. Asynchronous ML (face count + face area + sunglasses) — lazy-
 *      loads heavy deps on first call; ~1-3s warm-up then ~200-500ms
 *      per check, runs in `validatePhotoAsync`. Caller shows a spinner
 *      overlay while this resolves.
 *
 *      - face-api.js TinyFaceDetector — counts faces + measures the
 *        first face's bounding box as % of frame.
 *      - @huggingface/transformers ONNX MobileNetV4 — top-20 ImageNet
 *        keyword-match for sunglasses.
 *
 * Graceful degradation: if EITHER ML stack throws (CDN dead, browser too
 * old, timeout) we allow the upload through with `faceConfirmed: false`
 * — the server-side provider catches issues at image-gen step and the
 * existing refund flow covers any spoiled run. Never block the user over
 * a local ML failure.
 *
 * We never sniff bytes for "magic numbers" — browsers reliably set
 * `File.type` from extension, and our server re-validates. False positives
 * here block legitimate uploads; false negatives are caught downstream.
 *
 * Keep these constants in lock-step with server. If you change either
 * limit here, change `api/src/routes/generations.ts ALLOWED_MIMES /
 * MAX_PHOTO_BYTES` to match — divergence means "passes on frontend, 400
 * on server", which is the worst UX.
 */

import { detectFaces } from './faceDetection';
import { detectGlasses } from './sunglassesDetection';

/** Server mirror — JPEG + PNG only. WebP/HEIC dropped (see server comment). */
export const ALLOWED_MIMES = new Set(['image/jpeg', 'image/png']);
/** Server mirror — 10 MB tightest downstream limit (Telegram album mp). */
export const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

/**
 * Minimum face-area threshold. Faces smaller than this share too few pixels
 * for the AI to preserve identity — kie.ai's Nano Banana visibly degrades
 * below ~5%. Empirically tuned on test uploads.
 */
const MIN_FACE_AREA_PCT = 5;

export type PhotoError =
  | { kind: 'wrong_type'; receivedType: string; receivedName: string }
  | { kind: 'too_large'; sizeMB: number; maxMB: number }
  | { kind: 'no_face' }
  | { kind: 'multiple_faces'; count: number }
  | { kind: 'face_too_small'; areaPct: number }
  | { kind: 'sunglasses' };

export interface ValidationOk {
  ok: true;
  /** True when ML actually ran and confirmed an acceptable face. False
   *  when ML stack failed gracefully (CDN dead, decode failed). */
  faceConfirmed: boolean;
}
export interface ValidationFail {
  ok: false;
  error: PhotoError;
}
export type ValidationResult = ValidationOk | ValidationFail;

/**
 * Synchronous quick-gates (mime + size). Returns immediately. Call this
 * first — only run async ML if this passes (otherwise we'd waste time
 * decoding a file we're going to reject anyway).
 */
export function validatePhotoSync(file: File): ValidationFail | { ok: true } {
  // Belt + suspenders on MIME: some Android browsers don't populate
  // file.type for camera captures, so we fall back to the filename
  // extension when type is empty. JPEG / PNG-only.
  const mime = file.type || mimeFromName(file.name);
  if (!ALLOWED_MIMES.has(mime)) {
    return {
      ok: false,
      error: {
        kind: 'wrong_type',
        receivedType: file.type || extensionOf(file.name) || 'unknown',
        receivedName: file.name,
      },
    };
  }
  if (file.size > MAX_PHOTO_BYTES) {
    return {
      ok: false,
      error: {
        kind: 'too_large',
        sizeMB: Math.round((file.size / (1024 * 1024)) * 10) / 10,
        maxMB: MAX_PHOTO_BYTES / (1024 * 1024),
      },
    };
  }
  return { ok: true };
}

/**
 * Async ML validation: face count, face area %, sunglasses. Runs assuming
 * sync gates passed (caller is responsible for calling validatePhotoSync
 * first).
 *
 * Returns:
 *   - { ok: true, faceConfirmed: true } — ML ran, file passed all checks
 *   - { ok: true, faceConfirmed: false } — ML stack failed gracefully;
 *     server is the final gate
 *   - { ok: false, error: ... } — ML ran, file rejected for a fixable
 *     reason (no_face / multiple_faces / face_too_small / sunglasses)
 *
 * face-api.js and HF transformers are both lazy-imported via their own
 * modules — main bundle doesn't grow. First call per session pays the
 * warm-up cost (~1-3s); subsequent calls reuse the loaded runtime.
 */
export async function validatePhotoAsync(file: File): Promise<ValidationResult> {
  // ─── Face check (face-api.js TinyFaceDetector) ───────────────────
  let faces;
  try {
    faces = await detectFaces(file);
  } catch (e) {
    // Model load / decode / inference failed — could be CDN down, browser
    // too old, corrupt file (sync gate didn't catch). Treat as "ML
    // unavailable, don't block". Server still catches no-face at image
    // gen step (kie.ai Nano Banana fails clearly on no-face inputs).
    console.warn('[photoValidation] face-api failed, allowing upload', e);
    return { ok: true, faceConfirmed: false };
  }

  if (faces.faceCount === 0) {
    return { ok: false, error: { kind: 'no_face' } };
  }
  if (faces.faceCount > 2) {
    // 2 is OK (selfie with someone, kie picks the largest). 3+ suggests
    // a group photo / magazine cover; pre-warn so the user can swap.
    return { ok: false, error: { kind: 'multiple_faces', count: faces.faceCount } };
  }
  if (
    faces.faceAreaPercent !== undefined &&
    faces.faceAreaPercent < MIN_FACE_AREA_PCT
  ) {
    return {
      ok: false,
      error: { kind: 'face_too_small', areaPct: faces.faceAreaPercent },
    };
  }

  // ─── Sunglasses check (HF transformers MobileNetV4) ──────────────
  // Non-fatal: if HF fails we still keep faceConfirmed=true from the
  // face-api pass (we DID validate the face, just not glasses). This
  // way an HF outage doesn't downgrade the face-check result.
  try {
    const glasses = await detectGlasses(file);
    if (glasses.hasSunglasses) {
      return { ok: false, error: { kind: 'sunglasses' } };
    }
    // hasClearGlasses → allow silently. We could surface a warn tier
    // but Photo Slot's binary error/no-error UX doesn't have a slot for
    // it. Future: optional warning chip.
  } catch (e) {
    console.warn('[photoValidation] HF glasses detection failed, allowing upload', e);
  }

  return { ok: true, faceConfirmed: true };
}

/**
 * Combined entry point — sync gates then async ML. Use this when you
 * don't need to split the two phases (e.g. tests).
 *
 * PhotoSlot.tsx calls `validatePhotoSync` + `validatePhotoAsync` separately
 * so it can render preview + spinner between the two stages.
 */
export async function validatePhoto(file: File): Promise<ValidationResult> {
  const sync = validatePhotoSync(file);
  if (!sync.ok) return sync;
  return validatePhotoAsync(file);
}

/** Human-readable error copy for the per-slot banner. */
export function describePhotoError(error: PhotoError): { title: string; hint: string } {
  switch (error.kind) {
    case 'wrong_type':
      return {
        title: 'Unsupported file type',
        hint: `${error.receivedName} is ${error.receivedType}. We accept JPEG and PNG only.`,
      };
    case 'too_large':
      return {
        title: 'File is too large',
        hint: `${error.sizeMB} MB · maximum allowed is ${error.maxMB} MB. Try a smaller photo.`,
      };
    case 'no_face':
      return {
        title: 'No face detected',
        hint: "Our system couldn't find a face in this photo. Try a clearer shot with the face centered and visible.",
      };
    case 'multiple_faces':
      return {
        title: 'Multiple faces detected',
        hint: `Found ${error.count} faces. Use a photo where one person is clearly the subject.`,
      };
    case 'face_too_small':
      return {
        title: 'Face is too small',
        hint: `The face takes only ${error.areaPct}% of the frame — AI loses identity below ${MIN_FACE_AREA_PCT}%. Use a closer shot or crop tighter.`,
      };
    case 'sunglasses':
      return {
        title: 'Sunglasses detected',
        hint: 'The AI works best with clear faces. Try a photo without sunglasses.',
      };
  }
}

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────
function mimeFromName(name: string): string {
  const ext = extensionOf(name);
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  return '';
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}
